import React, { useState } from 'react'
import Modal from '../common/Modal'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { handlePatchRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { showToaster } from '../../utils/show-toaster.util'
import { formatTime } from '../../utils/date.util'
import { useMutation } from '@tanstack/react-query'

const SuspendCustomerModal = ({ isOpen, onClose, customer, customerId }) => {
  const [suspensionReason, setSuspensionReason] = useState('')

  const suspensionMutation = useMutation({
    mutationFn: () =>
      handlePatchRequest(`/admin/customers/${customerId}/suspend`, {
        suspensionReason,
      }),
    onSuccess: () => {
      QueryKeys.adminCustomerDetails.invalidate(customerId)
      QueryKeys.adminListCustomers.invalidateAll()
      showToaster('success', 'Customer suspended successfully')
      onClose()
    },
    onError: () => {
      showToaster('error', 'Failed to suspend customer')
    },
  })

  const handleConfirm = async () => {
    await suspensionMutation.mutateAsync()
  }
  if (!isOpen || !customer) return null

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="w-full max-w-[560px] bg-background rounded-lg p-4 flex flex-col items-center gap-4">
          {/* Icon */}
          <div className="w-[60px] h-[60px] flex items-center justify-center">
            <ExclamationTriangleIcon className="w-full h-full text-red-600" />
          </div>

          {/* Title */}
          <h2 className="text-center font-bold text-[17px] leading-tight">
            Suspend This Customer?
          </h2>

          {/* Description */}
          <p className="text-center text-[14px] text-black/70 max-w-[400px] leading-snug">
            Are you sure you want to suspend this customer? Their access to the platform will be
            restricted until further notice.
          </p>

          {/* Customer Details */}
          <div className="w-full flex border border-black/20 rounded-[4px] overflow-hidden">
            <div className="w-[140px] bg-secondary-100 border-r border-black/20 py-3 px-3 flex flex-col gap-2 text-sm">
              <span className="font-medium text-black">Name</span>
              <span className="font-medium text-black">Phone</span>
              <span className="font-medium text-black">Status</span>
              <span className="font-medium text-black">Created</span>
            </div>
            <div className="flex-1 bg-background py-3 px-3 flex flex-col gap-2 text-sm">
              <span className="font-medium text-black capitalize">{customer?.name}</span>
              <span className="font-medium text-black">{customer?.phoneNumber}</span>
              <span
                className={`font-medium ${
                  customer?.isActive ? 'text-success-600' : 'text-error-700'
                }`}
              >
                {customer?.isActive ? 'Active' : 'Suspended'}
              </span>
              <span className="font-medium text-black">{formatTime(customer?.createdAt)}</span>
            </div>
          </div>

          {/* Reason Input */}
          <div className="w-full flex flex-col gap-1">
            <label htmlFor="suspensionReason" className="text-sm font-medium text-gray-700">
              Reason for Suspension:
            </label>
            <input
              id="suspensionReason"
              value={suspensionReason}
              onChange={(e) => setSuspensionReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              placeholder="Enter reason"
            />
          </div>

          {/* Action Buttons */}
          <div className="w-full flex flex-col sm:flex-row justify-center gap-3 mt-2">
            <button
              onClick={handleConfirm}
              disabled={suspensionMutation.isLoading}
              className={`w-full sm:w-1/2 h-[44px] rounded-[8px] text-white font-medium text-sm bg-gradient-to-r from-error-700 to-error-800 ${
                suspensionMutation.isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {suspensionMutation.isLoading ? 'Suspending…' : 'Suspend'}
            </button>
            <button
              onClick={onClose}
              disabled={suspensionMutation.isLoading}
              className={`w-full sm:w-1/2 h-[44px] border border-danger rounded-[8px] text-error-900 font-medium text-sm hover:bg-neutral-200-100 ${
                suspensionMutation.isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default SuspendCustomerModal
